import React, { useMemo } from "react";
import {
  Beacon,
  AboutClassSection,
  ClassLocationSection,
  ClassTeacherSection,
  LatestReviewsSection,
  ResponsiveClassSection,
  BookingOptionsSection,
  RatingSummary,
  ProductFaqSection,
  CoursePageHeader,
  EmbedVideoCard,
  CoursePageTopBar,
  CoursePageFooterBar,
  ResponsiveClassCards,
  Icons,
  InfoBanner,
  bem
} from "@obby/components";
import { Gallery } from "~/components/Gallery";
import GoogleMaps from "../components/GoogleMaps";

import {
  CourseStatuses,
  CourseTypes,
  DashboardPermissions
} from "@obby/constants";
// libs
import {
  DashedString__UpperCaseOnEveryWordString,
  ImageUrl__ResizedImageUrl,
  Course__Price,
  Course__IsPriceFrom,
  TermCourse__NumberOfWeeks,
  Rating__RoundedRating
} from "@obby/lib";
import { scrollTo } from "~/lib/scrollTo";

import { styles } from "./InPersonClassPage.styles";
import AuthenticationState from "~/state/AuthenticationState";

const cn = bem("in-person-class-page");
export default function InPersonClassPage(props) {
  const {
    class: {
      title,
      isOnline,
      description,
      type,
      notes,
      reviews,
      schoolPlan,
      schoolName,
      schoolSlug,
      breadcrumbs,
      abilityLevel,
      requirements,
      isDBSChecked,
      teacherAvatar,
      acquiredSkills,
      shortDescription,
      galleryImages,
      requiredMaterial,
      requiresSendingPhysicalMaterial,
      schoolDescription,
      discount,
      faq = [],
      tags = [],
      summary,
      teacherOnlineImage,
      teacherName,
      isMultiplePrice,
      products,
      courses,
      results
    },
    class: course,
    locations,
    ticketTypes,
    googleMapURL,
    onAppointmentScheduleOptionSelect,
    onBuyAnObbyGiftCard,
    onGroupEnquire,
    onGiftThisClass,
    onSaveToWishlist,
    isSavedToWishList,
    onClickBreadcrumb,
    onDropInOptionSelect,
    onViewMoreReviews,
    onRegularOptionSelect,
    onGroupScheduleOptionSelect,
    onBlockBookingOptionSelect,
    onTrialOptionSelect,
    onJoinWaitingList,
    onTermOptionSelect
  } = props;

  const canEnquireLargeGroups =
    schoolPlan?.permissions?.find(
      permission => permission.name === DashboardPermissions.LARGE_GROUP_ENQUIRE
    )?.value || false;

  const menuActions = useMemo(
    () => [
      ...(course.status !== CourseStatuses.ARCHIVED
        ? [
            {
              text: summary.isActive ? (
                "Booking options"
              ) : (
                <>
                  View details
                  <br />
                  and book
                </>
              ),
              textMode: "uppercase",
              color: summary.isActive ? "robbinsEggBlue" : "azalea",
              onClick: summary.isActive
                ? () => scrollTo("booking-options")
                : onJoinWaitingList
            }
          ]
        : []),
      ...(props.class.status === CourseStatuses.APPROVED
        ? [
            {
              text: summary.hasAvailableDates
                ? "Gift this class"
                : "Buy a Giftcard",
              textMode: "uppercase",
              color: "robbinsEggBlue",
              type: "SECONDARY",
              onClick: summary.hasAvailableDates
                ? onGiftThisClass
                : onBuyAnObbyGiftCard,
              width: "auto"
            }
          ]
        : [])
    ],
    []
  );

  const location = locations.find(
    location => location._id === summary.locationId
  );

  let numberOfWeeks;
  if (type === CourseTypes.TERMLY && summary.hasAvailableDates) {
    numberOfWeeks = TermCourse__NumberOfWeeks(course);
  }
  return (
    <div className={`${cn()} ${styles.inPersonClassPage}`}>
      <div className={cn("wrapper")}>
        <div className={`${cn("header")} ${styles.header}`}>
          <div className="container-fluid">
            <CoursePageHeader
              isMultiplePrice={summary.isMultiplePrice}
              discount={discount}
              breadcrumbs={breadcrumbs}
              onClickBreadcrumb={onClickBreadcrumb}
              title={title}
              isOnline={isOnline}
              requiresSendingPhysicalMaterial={requiresSendingPhysicalMaterial}
              isEarlyAccess={summary.hasEarlyAccessDates}
              description={shortDescription}
              level={abilityLevel}
              rating={summary.rating}
              numberOfReviews={summary.numberOfReviews}
              location={
                !isOnline && course.status !== CourseStatuses.ARCHIVED
                  ? summary.location
                  : undefined
              }
              price={
                course.status !== CourseStatuses.ARCHIVED &&
                !course.isMarkedAsSoldOut
                  ? Course__Price(course)
                  : undefined
              }
              samePriceAlways={!Course__IsPriceFrom(course)}
              duration={
                [CourseTypes.CLASS, CourseTypes.WORKSHOP].includes(type)
                  ? summary.duration
                  : undefined
              }
              numberOfAdditionalDates={
                type === CourseTypes.COURSE
                  ? summary.nextAvailableAdditionalDates.length
                  : undefined
              }
              numberOfWeeks={numberOfWeeks}
              capacity={isOnline ? summary.capacity : undefined}
              nextSessionDate={summary.nextAvailableDate}
              images={galleryImages.map(image => image.url)}
              imagePosition={isOnline ? "left" : "right"}
              imageSize={isOnline ? "medium" : "large"}
              teacherImage={isOnline ? teacherOnlineImage : undefined}
              teacherName={isOnline ? teacherName : undefined}
              addToWishList
              isFavourite={isSavedToWishList}
              onToggleFavourite={onSaveToWishlist}
            />
          </div>
        </div>
        <div
          className={`${cn("top-bar")}
          ${styles.topBar({
            isOnSale: AuthenticationState.helpers.isOnSales()
          })}`}
        >
          <div className="container-fluid">
            <CoursePageTopBar
              numberOfReviews={summary.numberOfReviews}
              menuItems={[
                {
                  label: "About",
                  link: "#about"
                },
                ...(faq.length > 0 ? [{ label: "FAQs", link: "#faq" }] : []),
                ...(summary.numberOfReviews > 0
                  ? [
                      {
                        label: "Reviews",
                        link: "#latest-reviews"
                      }
                    ]
                  : []),
                ...(course.status !== CourseStatuses.ARCHIVED
                  ? [
                      {
                        label: "Your Teacher",
                        link: "#teacher"
                      },
                      {
                        label: "Location",
                        link: "#location"
                      }
                    ]
                  : [])
              ]}
              menuActions={menuActions}
            />
          </div>
        </div>
        <div className={`${cn("footer-bar")} ${styles.footerBar}`}>
          <CoursePageFooterBar
            discount={discount}
            price={Course__Price(course)}
            isPriceFrom={Course__IsPriceFrom(course)}
            rating={summary.rating}
            numberOfReviews={summary.numberOfReviews}
            menuActions={menuActions}
          />
        </div>

        <div className="container-fluid mt-4">
          {/* ABOUT CLASS */}
          <div
            className={`${cn("section")}
            ${styles.section({ isOnSale: discount })}`}
          >
            <Beacon id="about" />
            <ResponsiveClassSection title="What happens in this class?">
              <AboutClassSection
                notes={notes}
                description={description}
                acquiredSkills={acquiredSkills}
                requiredMaterial={requiredMaterial}
              />
            </ResponsiveClassSection>
          </div>

          {type === CourseTypes.TERMLY &&
            course.schoolId !== "msQEFXvmvRbrGdXXT" && (
              <div
                className={`${cn("section")} ${styles.section({
                  isOnSale: discount !== undefined
                })}`}
              >
                <InfoBanner
                  title="How does a termly booking work?"
                  sections={[
                    {
                      icon: Icons.ThreePeopleThin,
                      subTitle: "Pro rata availability",
                      description:
                        "You can book at the start of the term or at anytime during, with pro rata pricing automatically set"
                    },
                    {
                      icon: Icons.Cutlery,
                      subTitle: "Trial Sessions",
                      description:
                        "Look out for one-off trial sessions. When made available, you can book a trial before committing to the rest of the term."
                    },
                    {
                      icon: Icons.BulbThin,
                      subTitle: "Flexibility in your hands",
                      description:
                        "It is up to you how many weeks you join, but don’t leave it too long, as you may risk sessions selling out!"
                    }
                  ]}
                />
              </div>
            )}
          {faq.length > 0 && (
            <div
              className={`${cn("section")} ${styles.section({
                variant: "faq"
              })} ${styles.section({ isOnSale: discount })}`}
            >
              <Beacon id="faq" />
              <ResponsiveClassSection title="FAQs">
                <ProductFaqSection faq={faq} />
              </ResponsiveClassSection>
            </div>
          )}
          {isOnline && (
            <div className={`${cn("section")} ${styles.section}`}>
              <EmbedVideoCard
                title="How online classes work on obby"
                description="Teacher and students meet in a video chat session. Explore new interests and discover whether a teacher's style works well for you in the comfort of your own home!"
                embedHtml="<iframe src='https://player.vimeo.com/video/405489090' width='640' height='480' frameBorder='0' allow='autoplay; fullscreen' allowFullScreen></iframe>"
              />
            </div>
          )}
          {/* LATEST REVIEWS */}
          {summary.numberOfReviews > 0 && (
            <div
              className={`${cn("section")} ${styles.section({
                isOnSale: discount
              })}`}
            >
              <Beacon id="latest-reviews" />
              <ResponsiveClassSection
                title="Latest Reviews"
                subtitle={
                  summary.rating > 0 && (
                    <RatingSummary
                      rating={summary.rating}
                      reviewCount={summary.numberOfReviews}
                      primaryColor={"emeraldish"}
                    />
                  )
                }
              >
                <LatestReviewsSection
                  reviews={reviews.map(review => ({
                    date: review.createdAt,
                    review: review.reviewTxt,
                    name: review.name,
                    firstName: review.firstName,
                    lastName: review.lastName,
                    showOnlyFirstName: review.showOnlyFirstName,
                    avatarUrl: review.avatar,
                    rating: review.reviewValue
                  }))}
                  onReadMoreClick={onViewMoreReviews}
                />
              </ResponsiveClassSection>
            </div>
          )}

          <div
            className={`${cn("section")} ${styles.section({
              isOnSale: discount
            })}`}
          >
            <Beacon id="booking-options" />
            <ResponsiveClassSection title="Booking Options">
              {course.status === CourseStatuses.ARCHIVED && (
                <p className={styles.archiveNote()}>
                  This class is no longer bookable. View similar classes below
                  or explore the Obby marketplace{" "}
                  <a
                    target="_blank"
                    href="https://obby.co.uk"
                    className={styles.link()}
                  >
                    here
                  </a>
                  .
                </p>
              )}
              <BookingOptionsSection
                course={course}
                age={requirements}
                isDBSChecked={isDBSChecked}
                onDropInSelect={onDropInOptionSelect}
                onAppointmentSelect={onAppointmentScheduleOptionSelect}
                onRegularSelect={onRegularOptionSelect}
                onProRataTermSelect={onRegularOptionSelect}
                onGroupScheduleSelect={onGroupScheduleOptionSelect}
                onBundleSelect={onBlockBookingOptionSelect}
                onTrialSelect={onTrialOptionSelect}
                onGroupSelect={
                  canEnquireLargeGroups ? onGroupEnquire : undefined
                }
                onFullTermSelect={onTermOptionSelect}
              />
            </ResponsiveClassSection>
          </div>

          {/* Tag sections*/}
          {tags.length > 0 && (
            <div
              className={`${cn("section")} ${styles.section({
                isOnSale: discount
              })}`}
            >
              <Beacon id="tags" />
              <ResponsiveClassSection title="Tags">
                <div
                  className={`${cn("tag-list-wrapper")} ${
                    styles.tagListWrapper
                  }`}
                >
                  {tags.map(tag => {
                    return (
                      <a
                        className={`${cn("tag-list-item")} ${
                          styles.tagListItem
                        } mx-2`}
                        key={tag.slug}
                        href={`/learn/${tag.slug}`}
                        draggable={false}
                        onDragStart={e => e.preventDefault()}
                      >
                        {DashedString__UpperCaseOnEveryWordString(tag.slug)}
                      </a>
                    );
                  })}
                </div>
              </ResponsiveClassSection>
            </div>
          )}
          {/* CLASS TEACHER SECTION */}
          {course.status !== CourseStatuses.ARCHIVED && (
            <div
              className={`${cn("section")} ${styles.section({
                isOnSale: discount
              })}`}
            >
              <Beacon id="teacher" />
              <ResponsiveClassSection title="Your Teacher">
                <ClassTeacherSection
                  teacherName={schoolName}
                  teacherDescription={schoolDescription}
                  teacherUrl={`/schools/${schoolSlug}`}
                  teacherAvatar={teacherAvatar}
                />
              </ResponsiveClassSection>
            </div>
          )}
          {/* MAP */}
          {!isOnline && course.status !== CourseStatuses.ARCHIVED && (
            <div
              className={`${cn("section")} ${styles.section({
                isOnSale: discount
              })}`}
            >
              <Beacon id="location" />
              <ResponsiveClassSection title="Class Location" fluid>
                <ClassLocationSection
                  googleMap={
                    location?.geolocation && (
                      <GoogleMaps
                        googleMapURL={googleMapURL}
                        zoom={15}
                        lat={location?.geolocation.lat}
                        lng={location?.geolocation.lng}
                      />
                    )
                  }
                  classType={type}
                  location={location}
                  isAnywhere={summary.isAnywhere}
                  hasVariousLocations={summary.hasVariousLocations}
                />
              </ResponsiveClassSection>
            </div>
          )}
          {/* GALLERY */}
          {!isOnline && (
            <ResponsiveClassSection
              type="gallery"
              title={`Gallery (${props.class.galleryImages.length})`}
            >
              {props.class.galleryImages &&
                props.class.galleryImages.length > 0 && (
                  <div className={`${cn("section")} ${styles.section}`}>
                    <div className={`${cn("area")}`}>
                      <div className={`${cn("area-body")}`}>
                        <Gallery images={props.class.galleryImages} />
                      </div>
                    </div>
                  </div>
                )}
            </ResponsiveClassSection>
          )}

          <div className={`${cn("section")} ${styles.section}`}>
            <ResponsiveClassCards
              title={`More from ${schoolName}`}
              cards={courses.map(course => ({
                addToWishList: false,
                title: course.title,
                thumbnail: course.images.length
                  ? ImageUrl__ResizedImageUrl(
                      { width: 450, height: 300, crop: "fill" },
                      course.images[0].url
                    )
                  : undefined,
                rating: Rating__RoundedRating(course.summary.rating),
                numberOfReviews: course.summary.numberOfReviews,
                location: course.summary.location,
                price: course.summary.price,
                samePriceAlways: course.summary.additionalPrices.length === 0,
                url: `/classes/${course.category}/${course.subcategory}/${course.slug}`,
                discount: course.discount
              }))}
              numberOfColumns={4}
            />
          </div>
          {props.results.length > 0 && (
            <div className={`${cn("section")} ${styles.section}`}>
              <ResponsiveClassCards
                title="You may also like"
                railCarouselMobile={true}
                cards={props.results.map(course => ({
                  addToWishList: false,
                  title: course.title,
                  thumbnail: ImageUrl__ResizedImageUrl(
                    { width: 450, height: 300, crop: "fill" },
                    course.image.url
                  ),
                  rating: Rating__RoundedRating(course.rating),
                  numberOfReviews: course.reviewsCount,
                  location: course.location,
                  price: `${course.price}00`,
                  url: `${course.itemURL}`,
                  discount: course.discount
                }))}
                numberOfColumns={12}
                mobileColumns={6}
                tabletColumns={9}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
